import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { absoluteUrl } from "@/lib/site-config";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { openCookieSettingsModal } from "@/lib/cookie-consent";
import LegalTocMobile from "@/components/legal/LegalTocAside";
import LegalTocFixed from "@/components/legal/LegalTocFixed";

const purple = "#4A3A8A";
const lavender = "#f0eeff";
const textBody = "#3a3555";

function Badge({ kind, children }) {
  const map = {
    essential: "bg-[#e8f5e9] text-[#2e7d32]",
    analytics: "bg-[#fff8e1] text-[#f57f17]",
    functional: "bg-[#e3f2fd] text-[#1565c0]",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${map[kind]}`}>
      {children}
    </span>
  );
}

function CookieCard({ row }) {
  return (
    <li className="rounded-xl border border-violet-light/60 bg-white px-4 py-3 shadow-[0_1px_4px_rgba(74,58,138,0.05)]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <code className="font-mono text-[11.5px] font-semibold text-violet-oscuro break-all">{row.name}</code>
        <Badge kind={row.kind}>{row.label}</Badge>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed" style={{ color: textBody }}>{row.purpose}</p>
      <p className="mt-2 border-t border-violet-light/50 pt-2 text-[11.5px] text-ink-3">
        <span className="font-semibold text-ink-2">Duración:</span> {row.duration}
      </p>
    </li>
  );
}

function CookieList({ rows }) {
  return (
    <ul className="mt-4 list-none space-y-3 p-0">
      {rows.map((row) => <CookieCard key={row.name} row={row} />)}
    </ul>
  );
}

function SectionShell({ id, num, title, children, className = "" }) {
  return (
    <section
      id={id}
      className={`scroll-mt-28 rounded-2xl border border-violet-light/80 bg-white p-6 shadow-sm sm:p-8 ${className}`}
    >
      <div className="mb-5 flex items-start gap-4 border-b border-violet-light/60 pb-5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-fuerte text-sm font-bold text-white shadow-[0_4px_14px_-6px_rgba(74,58,138,0.5)]">
          {num}
        </div>
        <h2 className="pt-0.5 font-display text-lg font-bold leading-snug tracking-tight text-ink sm:text-[1.25rem]">
          {title}
        </h2>
      </div>
      <div className="text-[14.5px] leading-[1.78]" style={{ color: textBody }}>
        {children}
      </div>
    </section>
  );
}

function InfoBox({ accent = false, children }) {
  return (
    <div
      className="my-4 rounded-xl border px-5 py-4 text-[13.5px] leading-relaxed"
      style={{
        background: accent ? "rgba(201,162,39,0.07)" : lavender,
        borderColor: accent ? "rgba(201,162,39,0.35)" : "#e4dffe",
        borderLeft: `4px solid ${accent ? "#c9a227" : purple}`,
        color: accent ? "#5a4200" : textBody,
      }}
    >
      {children}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="my-3 list-none space-y-2">
      {items.map((t, i) => (
        <li
          key={i}
          className="relative pl-5 text-[14.5px] leading-relaxed before:absolute before:left-0 before:top-[0.62rem] before:h-1.5 before:w-1.5 before:rounded-full before:bg-gold"
          style={{ color: textBody }}
        >
          {t}
        </li>
      ))}
    </ul>
  );
}

const tocItems = [
  { href: "#s1", n: "1", label: "¿Qué son las cookies?" },
  { href: "#s2", n: "2", label: "Cookies que utilizamos" },
  { href: "#s3", n: "3", label: "Cookies de terceros" },
  { href: "#s4", n: "4", label: "Base legal" },
  { href: "#s5", n: "5", label: "Cómo gestionar las cookies" },
  { href: "#s6", n: "6", label: "Conservación de datos" },
  { href: "#s7", n: "7", label: "Tus derechos" },
  { href: "#s8", n: "8", label: "Contacto" },
];

const essentialRows = [
  { name: "ekk_session", kind: "essential", label: "Esencial", purpose: "Mantiene la sesión activa del usuario autenticado", duration: "Sesión" },
  { name: "ekk_auth_token", kind: "essential", label: "Esencial", purpose: "Token de autenticación segura (JWT)", duration: "30 días" },
  { name: "ekk_csrf", kind: "essential", label: "Esencial", purpose: "Protección contra ataques CSRF", duration: "Sesión" },
  { name: "ekk_cookies_consent", kind: "essential", label: "Esencial", purpose: "Almacena tus preferencias de cookies", duration: "12 meses" },
];

const analyticsRows = [
  { name: "_ga", kind: "analytics", label: "Analítica", purpose: "Distingue usuarios únicos (Google Analytics)", duration: "2 años" },
  { name: "_ga_*", kind: "analytics", label: "Analítica", purpose: "Almacena el estado de la sesión (Google Analytics)", duration: "2 años" },
];

const functionalRows = [
  { name: "ekk_lang", kind: "functional", label: "Funcional", purpose: "Guarda el idioma seleccionado por el usuario", duration: "12 meses" },
  { name: "ekk_ui_prefs", kind: "functional", label: "Funcional", purpose: "Preferencias de visualización (tema, densidad)", duration: "12 meses" },
];

export default function CookiesPolicyPage() {
  const sentinelRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#f4f3f9] text-ink">
      <Seo
        title="Política de cookies"
        description="Política de cookies de Ekklesia: tipos de cookies, terceros (Stripe, Analytics), base legal LSSI/RGPD y cómo gestionar tus preferencias."
        path="/cookies"
        ogType="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Política de cookies | Ekklesia",
          description: "Uso de cookies en ekklesiaonline.app y opciones de consentimiento.",
          url: absoluteUrl("/cookies"),
          isPartOf: { "@type": "WebSite", name: "Ekklesia", url: absoluteUrl("/") },
        }}
      />
      <Header />

      <main className="mx-auto max-w-7xl px-4 pb-28 pt-24 sm:px-6 sm:pt-28 md:px-10 md:pt-32 lg:px-12">
        {/* Wrapper: reserva espacio derecho para el TOC fijo en lg+ */}
        <div className="lg:pr-[calc(16rem+2.5rem)]">

        {/* ── Page header ────────────────────────────────────── */}
        <header>
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-gold">Legal</p>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-[2.2rem] lg:leading-tight">
            Política de Cookies
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-2">
            Información sobre las cookies que utilizamos en Ekklesia y cómo puedes gestionarlas.
          </p>
          <div className="mt-7 grid gap-3 sm:grid-cols-3 sm:gap-4">
            {[
              { label: "Actualización", value: "Enero 2025" },
              { label: "Responsable", value: "Osmel Enamorado" },
              { label: "Ámbito", value: "España / UE" },
            ].map((c) => (
              <div key={c.label} className="rounded-xl border border-violet-light/80 bg-white/90 px-4 py-3 shadow-sm">
                <p className="text-[11px] font-bold uppercase tracking-wide text-ink-4">{c.label}</p>
                <p className="mt-1 font-semibold text-ink">{c.value}</p>
              </div>
            ))}
          </div>
        </header>

        {/* Mobile TOC */}
        <LegalTocMobile ariaLabel="Índice de la política de cookies" tocItems={tocItems} />

        {/* ── Article ── */}
        <div className="mt-10 space-y-6">

            {/* s1 */}
            <SectionShell id="s1" num="1" title="¿Qué son las cookies?">
              <p className="mb-3.5">
                Las cookies son pequeños archivos de texto que los sitios web almacenan en tu dispositivo cuando los
                visitas. Permiten que el sitio recuerde tus acciones y preferencias para que no tengas que volver a
                introducirlas cada vez.
              </p>
              <p className="mb-3.5">
                EkklesiaOnline usa cookies propias y de terceros para garantizar el correcto funcionamiento de la
                plataforma, mejorar la experiencia de usuario y analizar el uso del servicio.
              </p>
              <InfoBox>
                <strong className="text-violet-fuerte">¿Por qué te informamos?</strong> La normativa española (LSSI-CE) y
                el RGPD nos obligan a informarte sobre el uso de cookies y obtener tu consentimiento cuando sea necesario.
                Relacionado:{" "}
                <Link to="/privacidad" className="font-semibold text-violet-fuerte underline underline-offset-2 hover:text-violet-oscuro">
                  Política de privacidad
                </Link>
                .
              </InfoBox>
            </SectionShell>

            {/* s2 */}
            <SectionShell id="s2" num="2" title="Cookies que utilizamos">
              <p className="mb-5">
                A continuación detallamos las cookies de EkklesiaOnline, clasificadas por tipo y finalidad:
              </p>
              <div className="grid gap-5 lg:grid-cols-3">
                <div className="flex flex-col rounded-xl border border-violet-light/80 bg-[#faf9ff] p-5">
                  <h3 className="font-bold text-violet-fuerte">Estrictamente necesarias</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-ink-3">
                    Imprescindibles para el funcionamiento. No requieren consentimiento.
                  </p>
                  <CookieList rows={essentialRows} />
                </div>
                <div className="flex flex-col rounded-xl border border-violet-light/80 bg-[#faf9ff] p-5">
                  <h3 className="font-bold text-violet-fuerte">Analíticas</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-ink-3">
                    Nos permiten conocer cómo se usa el sitio. Requieren tu consentimiento.
                  </p>
                  <CookieList rows={analyticsRows} />
                </div>
                <div className="flex flex-col rounded-xl border border-violet-light/80 bg-[#faf9ff] p-5">
                  <h3 className="font-bold text-violet-fuerte">Funcionales</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-ink-3">
                    Mejoran la experiencia recordando preferencias. Requieren tu consentimiento.
                  </p>
                  <CookieList rows={functionalRows} />
                </div>
              </div>
            </SectionShell>

            {/* s3 */}
            <SectionShell id="s3" num="3" title="Cookies de terceros">
              <p className="mb-4">
                Integramos servicios de terceros que pueden establecer sus propias cookies, sujetos a sus propias
                políticas:
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-violet-light/80 p-5" style={{ background: lavender }}>
                  <p className="font-bold text-violet-oscuro">Stripe</p>
                  <p className="mt-2 text-[14px] leading-relaxed">
                    Procesador de pagos. Cookies para prevención del fraude y seguridad en transacciones.{" "}
                    <a href="https://stripe.com/es/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-fuerte hover:underline">
                      Política de Stripe
                    </a>
                  </p>
                </div>
                <div className="rounded-xl border border-violet-light/80 p-5" style={{ background: lavender }}>
                  <p className="font-bold text-violet-oscuro">Google Analytics</p>
                  <p className="mt-2 text-[14px] leading-relaxed">
                    Análisis del uso (si está activado). Datos tratados de forma agregada.{" "}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-fuerte hover:underline">
                      Política de Google
                    </a>
                  </p>
                </div>
              </div>
              <InfoBox accent>
                <strong className="text-[#7a5800]">Nota importante:</strong> No vendemos ni compartimos datos de cookies
                con terceros con fines publicitarios. Las integraciones listadas se limitan a lo operativo del servicio.
              </InfoBox>
            </SectionShell>

            {/* s4 */}
            <SectionShell id="s4" num="4" title="Base legal">
              <p className="mb-4">El tratamiento mediante cookies se fundamenta en:</p>
              <BulletList items={[
                <><strong>Cookies esenciales:</strong> Interés legítimo y ejecución del contrato (art. 6.1.b RGPD). No requieren consentimiento al ser necesarias para el servicio.</>,
                <><strong>Cookies analíticas y funcionales:</strong> Consentimiento expreso (art. 6.1.a RGPD), recabado mediante el banner o el panel de configuración.</>,
              ]} />
              <p className="mt-3">
                Puedes revocar tu consentimiento en cualquier momento sin que ello afecte a la prestación del servicio
                principal.
              </p>
            </SectionShell>

            {/* s5 */}
            <SectionShell id="s5" num="5" title="Cómo gestionar las cookies">
              <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-violet-light/80 bg-gradient-to-br from-[#FAFAFE] to-white p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7">
                <div className="min-w-0">
                  <h3 className="font-display text-base font-bold text-ink">Desde este sitio</h3>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-ink-2">
                    Abre el panel de preferencias en cualquier momento para activar o desactivar categorías opcionales.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openCookieSettingsModal}
                  className="inline-flex h-11 shrink-0 items-center justify-center rounded-xl bg-violet-fuerte px-5 text-[14px] font-semibold text-white shadow-[0_4px_16px_-6px_rgba(74,58,138,0.45)] transition-colors hover:bg-violet-oscuro focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-fuerte"
                >
                  Configurar cookies
                </button>
              </div>

              <h3 className="mb-2 font-bold text-violet-fuerte">Desde tu navegador</h3>
              <p className="mb-4">
                También puedes gestionar o eliminar cookies directamente. Deshabilitar algunas puede afectar al
                funcionamiento del servicio:
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  { name: "Google Chrome", path: "Configuración → Privacidad y seguridad → Cookies" },
                  { name: "Mozilla Firefox", path: "Opciones → Privacidad y seguridad → Cookies" },
                  { name: "Safari", path: "Preferencias → Privacidad → Gestionar datos de sitios web" },
                  { name: "Microsoft Edge", path: "Configuración → Privacidad, búsqueda y servicios → Cookies" },
                ].map((b) => (
                  <div key={b.name} className="rounded-xl border border-violet-light/70 bg-white px-4 py-3 shadow-sm">
                    <p className="text-[13.5px] font-bold text-violet-oscuro">{b.name}</p>
                    <p className="mt-1 text-[12.5px] leading-snug text-ink-3">{b.path}</p>
                  </div>
                ))}
              </div>
              <InfoBox>
                <strong className="text-violet-fuerte">Importante:</strong> Si eliminas las cookies de sesión, se cerrará
                tu sesión activa en EkklesiaOnline.
              </InfoBox>
            </SectionShell>

            {/* s6 */}
            <SectionShell id="s6" num="6" title="Conservación de datos">
              <p className="mb-3.5">
                Los datos recabados se conservan durante el tiempo indicado en el apartado 2, salvo que los elimines antes.
              </p>
              <p className="mb-3.5">
                Los datos analíticos anonimizados pueden conservarse hasta 26 meses (política de Google Analytics).
              </p>
              <p>
                La preferencia de consentimiento se guarda en{" "}
                <code className="rounded px-1.5 py-0.5 font-mono text-[13px]" style={{ background: lavender }}>
                  ekk_cookies_consent
                </code>{" "}
                e incluye tus elecciones de categorías. Se renueva al guardar de nuevo o expira a los 12 meses.
              </p>
            </SectionShell>

            {/* s7 */}
            <SectionShell id="s7" num="7" title="Tus derechos">
              <p className="mb-4">
                En relación con los datos tratados mediante cookies, conforme al RGPD y la LOPDGDD, tienes derecho a:
              </p>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {[
                  { lead: "Acceder", rest: "a los datos tratados vía cookies." },
                  { lead: "Rectificar", rest: "datos inexactos o incompletos." },
                  { lead: "Suprimir", rest: "tus datos («derecho al olvido»)." },
                  { lead: "Oponerte", rest: "a cookies no esenciales." },
                  { lead: "Limitar", rest: "el tratamiento en determinadas circunstancias." },
                  { lead: "Retirar el consentimiento", rest: "en cualquier momento sin efecto retroactivo." },
                ].map((item) => (
                  <div
                    key={item.lead}
                    className="rounded-xl border border-[#e4dffe] px-4 py-3 text-[13.5px] leading-snug"
                    style={{ background: lavender, color: textBody }}
                  >
                    <strong className="text-violet-oscuro">{item.lead}</strong> {item.rest}
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[14px]">
                Puedes ejercer estos derechos en la dirección del apartado de contacto. También puedes reclamar ante la{" "}
                <strong>AEPD</strong> en{" "}
                <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-fuerte hover:underline">
                  www.aepd.es
                </a>
                .
              </p>
            </SectionShell>

            {/* s8 */}
            <SectionShell id="s8" num="8" title="Contacto">
              <div className="grid gap-6 lg:grid-cols-12">
                <div className="lg:col-span-7">
                  <h3 className="font-display text-base font-bold text-ink">¿Preguntas sobre cookies?</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-ink-2">
                    Contacta con el responsable del tratamiento para cualquier duda.
                  </p>
                  <p className="mt-3 text-[14px] leading-relaxed text-ink-2">
                    <strong className="text-ink">Responsable:</strong> Osmel Enamorado
                    <br />
                    <strong className="text-ink">Servicio:</strong> EkklesiaOnline
                    <br />
                    <strong className="text-ink">Web:</strong> ekklesiaonline.app
                  </p>
                  <p className="mt-4 text-[13px] leading-relaxed text-ink-3">
                    Respondemos en un máximo de 30 días hábiles. Para datos personales más amplios, consulta la{" "}
                    <Link to="/privacidad" className="font-semibold text-violet-fuerte underline underline-offset-2 hover:text-violet-oscuro">
                      política de privacidad
                    </Link>
                    .
                  </p>
                </div>
                <aside className="flex flex-col justify-center rounded-xl border border-violet-light/80 bg-[#faf9ff] p-5 sm:p-6 lg:col-span-5">
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-gold">Correo</p>
                  <a
                    href="mailto:privacidad@ekklesiaonline.app"
                    className="mt-3 break-all font-display text-base font-semibold text-violet-fuerte underline decoration-violet-fuerte/25 underline-offset-4 transition-colors hover:text-violet-oscuro"
                  >
                    privacidad@ekklesiaonline.app
                  </a>
                  <button
                    type="button"
                    onClick={openCookieSettingsModal}
                    className="mt-5 inline-flex h-10 items-center justify-center rounded-xl border border-violet-light/80 bg-white px-4 text-[13.5px] font-semibold text-violet-fuerte transition-colors hover:border-violet-fuerte/40 hover:bg-[#f4f3f9]"
                  >
                    Abrir configuración de cookies
                  </button>
                </aside>
              </div>
            </SectionShell>

            {/* Sentinel: cuando entra en viewport, el TOC fijo se oculta */}
            <div ref={sentinelRef} aria-hidden />
          </div>

        </div>{/* fin del wrapper lg:pr */}
      </main>

        <LegalTocFixed
          sentinelRef={sentinelRef}
          ariaLabel="Índice de la política de cookies"
          tocItems={tocItems}
        />

      <Footer />
    </div>
  );
}
